import { useEffect, useMemo, useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { AuthShell, PrimaryButton, fieldCls, labelCls } from "@/components/auth/AuthShell"
import { ensureSession } from "@/core/auth"
import { backendUrl } from "@/core/backend-url"
import { authApiPath } from "@/core/api-contract"

// OAuth consent screen for the MCP / OAuth Provider flow (Claude, ChatGPT, …).
// Better Auth's oauthProvider redirects the authenticated organizer here
// (auth.oauth.consentPage = ACCOUNT_URL + /oauth/consent) to approve a client.
// It lives as a TOP-LEVEL route (sibling of /sign-in) so it renders in the
// minimal AuthShell — NOT the org app shell with nav/sidebar. Ported from the
// old admin's OAuthConsentPage.
export const Route = createFileRoute("/oauth/consent")({
  component: OAuthConsentView,
})

type OAuthClient = {
  client_id?: string
  client_name?: string
  clientId?: string
  name?: string
}
type Organization = { id: string; name: string }

function OAuthConsentView() {
  const query = useMemo(() => new URLSearchParams(window.location.search), [])
  const clientId = query.get("client_id") ?? ""
  const scopes = (query.get("scope") ?? "").split(/\s+/).filter(Boolean)

  const [client, setClient] = useState<OAuthClient | null>(null)
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [orgId, setOrgId] = useState("")
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      // BA gates the authorize step on a session before redirecting here, but
      // guard anyway — bounce to sign-in and come back if the cookie's missing.
      const session = await ensureSession({ force: true })
      if (!session) {
        const next = `${window.location.pathname}${window.location.search}`
        window.location.assign(`/sign-in?${new URLSearchParams({ next })}`)
        return
      }
      try {
        const [clientRes, orgsRes] = await Promise.all([
          fetch(
            backendUrl(
              `${authApiPath("/oauth2/public-client")}?${new URLSearchParams({ client_id: clientId })}`,
            ),
            { credentials: "include" },
          ),
          fetch(backendUrl(authApiPath("/organization/list")), { credentials: "include" }),
        ])
        if (!clientRes.ok) throw new Error(await readError(clientRes))
        if (!orgsRes.ok) throw new Error(await readError(orgsRes))
        const clientBody = (await clientRes.json()) as OAuthClient
        const orgsBody = (await orgsRes.json()) as Organization[]
        if (cancelled) return
        setClient(clientBody)
        setOrgs(orgsBody)
        setOrgId(orgsBody[0]?.id ?? "")
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Unable to load authorization details")
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [clientId])

  async function decide(accept: boolean) {
    setBusy(true)
    setError(null)
    try {
      // Pin the active org so the granted token is scoped to the right workspace.
      if (accept && orgId) {
        const orgRes = await fetch(backendUrl(authApiPath("/organization/set-active")), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ organizationId: orgId }),
        })
        if (!orgRes.ok) throw new Error(await readError(orgRes))
      }
      const res = await fetch(backendUrl(authApiPath("/oauth2/consent")), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          accept,
          oauth_query: window.location.search.replace(/^\?/, ""),
        }),
      })
      if (!res.ok) throw new Error(await readError(res))
      const body = (await res.json()) as { url?: string; redirect_uri?: string }
      const redirectUrl = body.url ?? body.redirect_uri
      if (!redirectUrl) throw new Error("Authorization server returned no redirect")
      window.location.assign(redirectUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Authorization failed")
      setBusy(false)
    }
  }

  const clientName =
    client?.client_name ?? client?.name ?? client?.client_id ?? client?.clientId ?? clientId

  return (
    <AuthShell title="Authorize access" wave>
      <div className="space-y-5">
        <p className="text-[13.5px] leading-relaxed text-white/70">
          Allow <span className="font-semibold text-white">{clientName || "this app"}</span> to use
          Attend on your behalf. It acts through your account — limited to your role and the events
          you belong to.
        </p>

        {loading ? (
          <p className="text-[13px] text-white/55">Loading authorization details…</p>
        ) : (
          <>
            {orgs.length > 1 && (
              <label className="block">
                <span className={labelCls}>Workspace</span>
                <select
                  className={fieldCls}
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                >
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div className="rounded-xl border border-white/12 bg-white/[0.04] p-3">
              <p className="mb-2 text-[12px] font-semibold text-white/70">Requested access</p>
              <ul className="space-y-1 text-[13px] text-white/55">
                {(scopes.length ? scopes : ["Attend MCP tools"]).map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>

            {error && <p className="text-[13px] text-rose-300">{error}</p>}

            <div className="flex gap-3">
              <button
                type="button"
                disabled={busy}
                onClick={() => void decide(false)}
                className="h-10 flex-1 rounded-xl border border-white/15 text-[14px] font-semibold text-white/80 transition hover:bg-white/5 disabled:opacity-40"
              >
                Deny
              </button>
              <div className="flex-1">
                <PrimaryButton
                  type="button"
                  disabled={busy || (orgs.length > 1 && !orgId)}
                  onClick={() => void decide(true)}
                >
                  {busy ? "Authorizing…" : "Authorize"}
                </PrimaryButton>
              </div>
            </div>
          </>
        )}
      </div>
    </AuthShell>
  )
}

async function readError(response: Response): Promise<string> {
  const body = (await response.json().catch(() => null)) as
    | { message?: string; error?: string; error_description?: string }
    | null
  return (
    body?.message ??
    body?.error_description ??
    body?.error ??
    `${response.status} ${response.statusText}`
  )
}
